"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Box, Car, Code2, Settings, Sparkles, X } from "lucide-react";
import { WrenchMascot } from "./WrenchMascot";
import { WRENCH_GOALS, type WrenchGoal } from "./routes";

type Props = {
  open: boolean;
  onClose: () => void;
  onDoNotShowAgain: () => void;
  onSkip: () => void;
};

const ICON_MAP: Record<WrenchGoal["icon"], React.ComponentType<{ className?: string }>> = {
  props: Box,
  livery: Car,
  optimize: Settings,
  scripts: Code2,
};

export function WrenchWelcomeDialog({ open, onClose, onDoNotShowAgain, onSkip }: Props) {
  const router = useRouter();
  const [bubbleVisible, setBubbleVisible] = useState(false);

  useEffect(() => {
    if (!open) {
      setBubbleVisible(false);
      return;
    }
    // Tiny delay so the speech bubble feels like a reveal, not a flash.
    const id = window.setTimeout(() => setBubbleVisible(true), 250);
    return () => window.clearTimeout(id);
  }, [open]);

  if (!open) return null;

  const pickGoal = (goal: WrenchGoal) => {
    onClose();
    router.push(goal.destination);
  };

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center bg-background/75 px-4 backdrop-blur-md"
      onClick={onSkip}
    >
      <div
        className="relative grid w-full max-w-3xl grid-cols-1 gap-6 sm:grid-cols-[auto,1fr]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Mascot + speech bubble */}
        <div className="flex items-start justify-center gap-3 sm:flex-col sm:items-center sm:justify-start sm:pt-6">
          <WrenchMascot size={92} state="talking" />
          <div
            className={`relative max-w-[200px] rounded-2xl border border-accent/30 bg-accent/10 px-4 py-3 text-xs text-foreground transition-all ${
              bubbleVisible ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0"
            }`}
          >
            <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-accent">
              Wrench
            </span>
            <span className="mt-1.5 block leading-snug text-foreground/90">
              Welcome, Creator! What are we building today?
            </span>
          </div>
        </div>

        {/* Goal panel */}
        <div className="premium-panel relative rounded-[2rem] p-7">
          <button
            type="button"
            onClick={onSkip}
            className="absolute right-5 top-5 text-muted-foreground transition hover:text-foreground"
            aria-label="Close welcome dialog"
          >
            <X className="h-4 w-4" />
          </button>
          <p className="font-mono text-xs uppercase tracking-[0.24em] text-muted-foreground">
            Get started
          </p>
          <h2 className="mt-2 text-2xl font-semibold tracking-[-0.04em] text-foreground sm:text-3xl">
            Hey <span className="text-accent">Creator</span>, what are you looking to do?
          </h2>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Pick a goal and Wrench will route you to the fastest path.
          </p>

          <div className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {WRENCH_GOALS.map((goal) => {
              const Icon = ICON_MAP[goal.icon];
              return (
                <button
                  key={goal.id}
                  type="button"
                  onClick={() => pickGoal(goal)}
                  className="group flex items-center gap-3 rounded-2xl border border-border/70 bg-card/70 px-4 py-3.5 text-left transition duration-200 hover:-translate-y-0.5 hover:border-accent/40 hover:bg-accent/5"
                >
                  <span className="grid h-9 w-9 flex-none place-items-center rounded-xl border border-border/70 bg-secondary/60 text-foreground/80 transition group-hover:border-accent/40 group-hover:text-accent">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="flex-1 text-sm font-semibold tracking-tight text-foreground">
                    {goal.title}
                  </span>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => {
              onClose();
              router.push("/hub");
            }}
            className="mt-5 inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/60 px-3 py-1.5 text-[11px] text-muted-foreground transition hover:border-accent/35 hover:text-foreground"
          >
            <Sparkles className="h-3 w-3" />
            Open Studio Hub
          </button>

        </div>
      </div>
    </div>
  );
}
